import { fmtYmd, parseLocalYmd } from '~/constants/spotPrice'

/**
 * 1 回の取得で読める受渡日の最大日数 (開始日・終了日を含む)。 画面の期間欄の寄せ方は
 * design (`MAX_RANGE_DAYS`) の 31 日で、 backend の保護上限 (usecase の `maxPeriodDays`)
 * も同じ値にしてある。 超える期間は backend が InvalidArgument で拒否するので、
 * 画面は期間欄の段階でこの日数に収める。
 */
export const SPOT_PRICE_MAX_PERIOD_DAYS = 31

/** 実際に取得する受渡日の期間 (開始日・終了日とも含む、 `YYYY-MM-DD`)。 */
export type SpotPricePeriod = { from: string; to: string }

function addDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days)
}

/**
 * 開始日から {@link SPOT_PRICE_MAX_PERIOD_DAYS} 日以内に収めた終了日を返す。
 * 期間欄の変更 (開始日・終了日どちらを変えた時も) で終了日に通し、 31 日を超える期間を
 * 作らせない。 超えるなら開始日 + 30 日へ寄せる (欄の値も変わるので、 利用者に見える)。
 * 終了日が開始日より前・解釈できない値は触らない (その扱いは
 * {@link resolveSpotPricePeriod} が単日として決める)。
 */
export function limitPeriodEnd(dateFrom: string, dateTo: string): string {
  const from = parseLocalYmd(dateFrom)
  const to = parseLocalYmd(dateTo)
  if (from === null || to === null) return dateTo
  const last = addDays(from, SPOT_PRICE_MAX_PERIOD_DAYS - 1)
  return to.getTime() > last.getTime() ? fmtYmd(last) : dateTo
}

/**
 * 期間欄 (開始日・終了日) から、 実際に取得する期間を決める。
 *
 * - 開始日が解釈できなければ null (取得しない)。
 * - 終了日が開始日より前・解釈できない時は開始日 1 日だけ (期間欄は直さずに単日扱いにする
 *   既存の扱いと同じ。 `models/persistedFilters.ts` 参照)。
 * - 開始日から 31 日を超える終了日は 31 日目で止める (期間欄を {@link limitPeriodEnd} に
 *   通していない値の最後の砦)。
 */
export function resolveSpotPricePeriod(
  dateFrom: string,
  dateTo: string,
): SpotPricePeriod | null {
  const from = parseLocalYmd(dateFrom)
  if (from === null) return null
  const to = parseLocalYmd(dateTo)
  const last = addDays(from, SPOT_PRICE_MAX_PERIOD_DAYS - 1)
  let end = from
  if (to !== null && to.getTime() > from.getTime()) {
    end = to.getTime() > last.getTime() ? last : to
  }
  return { from: fmtYmd(from), to: fmtYmd(end) }
}
